const asNumber = value => Number(value) || 0;

export const getDateValue = value => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isInsidePeriod = (value, startDate, endDate) => {
  const date = getDateValue(value);
  if (!date) return false;
  if (startDate && date < new Date(startDate + 'T00:00:00')) return false;
  if (endDate && date > new Date(endDate + 'T23:59:59')) return false;
  return true;
};

export const getReservationCommission = (reservation, servicesById = {}) => {
  const service = servicesById[reservation.serviceId] || {};
  const rate = asNumber(reservation.commissionRate ?? service.commission);
  return {
    rate,
    amount: Math.max(0, asNumber(reservation.totalAmount) * rate / 100)
  };
};

export const isCommissionEligible = reservation => (
  reservation?.checkInStatus === 'checked_in' &&
  reservation?.paymentStatus === 'paid' &&
  !['cancelled', 'canceled'].includes(reservation?.status)
);

export const getAutomaticPayout = reservation => {
  const payments = Array.isArray(reservation?.payments) ? reservation.payments : [];
  return payments.reduce((sum, payment) => {
    const payoutConfirmed = payment.collaboratorPayoutStatus === 'paid' || payment.split?.collaboratorPaid === true;
    if (payment.method !== 'mercado_pago' || !payment.split || !payoutConfirmed) return sum;
    return sum + asNumber(payment.split.collaboratorCommission);
  }, 0);
};

export const buildCollaboratorFinance = ({
  reservations,
  services,
  entries,
  collaboratorEmail,
  startDate = '',
  endDate = ''
}) => {
  const normalizedEmail = String(collaboratorEmail || '').toLowerCase();
  const servicesById = Object.fromEntries(services.map(service => [service.id, service]));
  const ownReservations = reservations.filter(item => (
    String(item.createdByEmail || '').toLowerCase() === normalizedEmail
  ));
  const eligible = ownReservations.filter(isCommissionEligible).map(reservation => {
    const commission = getReservationCommission(reservation, servicesById);
    return {
      ...reservation,
      commissionRate: commission.rate,
      commissionAmount: commission.amount,
      automaticPayout: getAutomaticPayout(reservation)
    };
  });
  const filteredReservations = eligible.filter(item => isInsidePeriod(item.date, startDate, endDate));
  const ownEntries = entries.filter(entry => (
    entry.type === 'collaborator_payout' &&
    String(entry.collaboratorEmail || '').toLowerCase() === normalizedEmail
  ));
  const filteredEntries = ownEntries.filter(entry => isInsidePeriod(entry.date, startDate, endDate));
  const automaticEntries = filteredReservations
    .filter(item => item.automaticPayout > 0)
    .map(item => ({
      id: 'automatic-' + item.id,
      amount: item.automaticPayout,
      date: item.lastPaymentDate || item.date,
      description: 'Repasse automático · ' + (item.serviceName || 'Reserva'),
      method: 'Mercado Pago',
      reservationId: item.id
    }));
  const generated = filteredReservations.reduce((sum, item) => sum + item.commissionAmount, 0);
  const automaticPaid = filteredReservations.reduce((sum, item) => sum + item.automaticPayout, 0);
  const manualPaid = filteredEntries.reduce((sum, item) => sum + asNumber(item.amount), 0);

  return {
    reservations: filteredReservations.sort((a, b) => (getDateValue(b.date)?.getTime() || 0) - (getDateValue(a.date)?.getTime() || 0)),
    entries: filteredEntries.sort((a, b) => (getDateValue(b.date)?.getTime() || 0) - (getDateValue(a.date)?.getTime() || 0)),
    automaticEntries: automaticEntries.sort((a, b) => (getDateValue(b.date)?.getTime() || 0) - (getDateValue(a.date)?.getTime() || 0)),
    generated,
    automaticPaid,
    manualPaid,
    paid: automaticPaid + manualPaid,
    pending: Math.max(0, generated - automaticPaid - manualPaid)
  };
};
